import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output
} from '@angular/core';
import {
    loadStripe,
    Stripe,
    StripeCardNumberElement,
    StripeCardExpiryElement,
    StripeCardCvcElement
} from '@stripe/stripe-js';
import {PaymentService} from '../services/PaymentService';
import {OrderDetails} from '../../model/OrderDetails';
import {PaymentIntent} from '../../model/PaymentIntent';
import {Router} from '@angular/router';
import {STRIPE_KEY} from '../../../api.token';
import {FormControl, FormGroup, Validators, FormsModule} from '@angular/forms';
import {DecimalPipe, NgIf} from '@angular/common';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {FormElementComponent} from "../../../shared/forms/form-element/form-element.component";
import {FormTypes} from "../../../shared/forms/models/constants";
import {CheckboxModule} from "primeng/checkbox";
import {MessageModule} from "primeng/message";
import {CurrencyFormatPipe} from "../../../shared/pipes/CurrencyFormatPipe";

@Component({
    selector: 'app-pay',
    standalone: true,
    templateUrl: './pay.component.html',
    imports: [
        FormsModule,
        TranslatePipe,
        NgIf,
        FormElementComponent,
        CheckboxModule,
        MessageModule,
        CurrencyFormatPipe
    ],
    styleUrls: ['./pay.component.scss']
})
export class PayComponent implements OnInit, AfterViewInit {

    protected readonly FormTypes = FormTypes;

    @Input() isYearly: boolean = false;
    @Input() selectedPlan!: OrderDetails;
    @Input() creditAmount!: number;
    @Input() progressType!: string;
    @Output() paymentState: EventEmitter<boolean> = new EventEmitter<boolean>();

    stripe: Stripe | null = null;
    elements: any;

    isPayCredit: boolean = false;
    cardNumber?: StripeCardNumberElement;
    cardExpiry?: StripeCardExpiryElement;
    cardCvc?: StripeCardCvcElement;

    cardHolderName: string = '';

    price: number = 0;
    vat: number = 0.19;
    tax: number = 0;
    amount: number = 0;

    CREDITPAYMENT = 'creditPayment';

    // 🚦 Flag, um doppelte Aufrufe zu verhindern
    isProcessing = false;

    isPaymentFormValid = false;
    isAgbAccepted = false;

    payButtonEnabled = true;

    errorMessage: string | undefined = undefined;

    constructor(
        @Inject(STRIPE_KEY) private stripeKey: string,
        private paymentService: PaymentService,
        private translate: TranslateService
    ) {
    }

    formGroup = new FormGroup({
        agb: new FormControl<boolean>(false, Validators.requiredTrue)
    });

    ngOnInit(): void {
        this.isPayCredit = this.progressType.localeCompare(this.CREDITPAYMENT) === 0;

        if (this.isPayCredit) {
            this.price = this.creditAmount;
        } else {
            this.price = Number(
                this.isYearly ? this.selectedPlan.priceYearly : this.selectedPlan.priceMonthly
            );
        }


        this.tax = this.price * this.vat;
        this.amount = this.price + this.tax;

        this.formGroup.controls['agb'].valueChanges.subscribe(checked => {
            this.isAgbAccepted = !!checked;
        });
    }

    async ngAfterViewInit() {
        this.stripe = await loadStripe(this.stripeKey);
        if (this.stripe) {
            this.elements = this.stripe.elements();

            this.cardNumber = this.elements.create('cardNumber');
            this.cardNumber!.mount('#card-number-element');

            this.cardExpiry = this.elements.create('cardExpiry');
            this.cardExpiry!.mount('#card-expiry-element');

            this.cardCvc = this.elements.create('cardCvc');
            this.cardCvc!.mount('#card-cvc-element');

            this.cardNumber?.on('change', e => this.handleElementChange(e));
            this.cardExpiry?.on('change', e => this.handleElementChange(e));
            this.cardCvc?.on('change', e => this.handleElementChange(e));
        }
    }

    private handleElementChange(event: any) {
        if (event.error) {
            console.warn(event.error.message);

            return;
        }
        this.checkFormValidity();
    }

    checkFormValidity() {
        const nameValid = this.cardHolderName.trim().length > 0;

        const numberComplete = (this.cardNumber as any)?._complete;
        const expiryComplete = (this.cardExpiry as any)?._complete;
        const cvcComplete = (this.cardCvc as any)?._complete;

        const allComplete = numberComplete && expiryComplete && cvcComplete;

        this.isPaymentFormValid = allComplete;
    }

    async payCredit() {
        if (!this.checkRequiredFields()) {
            return;
        }
        if (!this.stripe || !this.cardNumber) return;
        if (this.isProcessing) return; // mehrfach-Klick verhindern
        this.isProcessing = true;
        this.payButtonEnabled = false;
        try {
            const creditIntent = await this.paymentService.createTopupIntent(this.price);
            const clientSecret = creditIntent.clientSecret;

            const {paymentIntent, error} = await this.stripe.confirmCardPayment(
                clientSecret,
                {
                    payment_method: {
                        card: this.cardNumber,
                        billing_details: {name: this.cardHolderName}
                    }
                }
            );

            if (error) {
                console.error(error);
                alert('Zahlung fehlgeschlagen');
            } else if (paymentIntent?.status === 'succeeded') {
                const pIntent: PaymentIntent = {
                    id: paymentIntent.id,
                    amount: paymentIntent.amount,
                    created: paymentIntent.created,
                    currency: paymentIntent.currency,
                    status: paymentIntent.status
                };

                this.paymentService.checkCreditInBackend(pIntent).subscribe({
                    next: () => this.paymentState.emit(true),
                    error: err => {
                        console.error(err);
                        alert('Prüfung im Backend fehlgeschlagen');
                    }
                });
            }
        } finally {
            this.isProcessing = false;
        }
    }

    checkRequiredFields() {
        this.errorMessage = undefined;
        this.checkFormValidity();

        const translateBasePath = "sipo.payment-component.pay-component";
        if (!this.isPaymentFormValid) {
            this.errorMessage = this.translate.instant(`${translateBasePath}.paymentdata-not-filled`);
            return false;
        } else if (!this.isAgbAccepted) {
            this.errorMessage = this.translate.instant(`${translateBasePath}.terms-not-accepted`);
            return false;
        }

        return true;
    }

    async pay() {

        if (!this.checkRequiredFields()) {
            return;
        }

        if (!this.stripe || !this.cardNumber) return;
        if (this.isProcessing) return;
        this.isProcessing = true;

        try {
            this.selectedPlan.isYearly = this.isYearly ?? false;
            this.selectedPlan.paymentMethod = 'card';
            const response = await this.paymentService.createPaymentIntent(this.selectedPlan);

            const {paymentIntent, error} = await this.stripe.confirmCardPayment(
                response.clientSecret,
                {
                    payment_method: {
                        card: this.cardNumber,
                        billing_details: {name: this.cardHolderName}
                    }
                }
            );

            if (error) {
                console.error(error);
                alert('Zahlung fehlgeschlagen');
            } else if (paymentIntent?.status === 'succeeded') {
                const pIntent: PaymentIntent = {
                    id: paymentIntent.id,
                    amount: paymentIntent.amount,
                    created: paymentIntent.created,
                    currency: paymentIntent.currency,
                    status: paymentIntent.status
                };

                this.paymentService.checkPaymentInBackend(pIntent).subscribe({
                    next: () => this.paymentState.emit(true),
                    error: err => {
                        console.error(err);
                        alert('Prüfung im Backend fehlgeschlagen');
                    }
                });
            }
        } finally {
            this.isProcessing = false;
        }
    }


    async createPaymentMethod() {
        if (!this.stripe || !this.cardNumber) return;

        this.payButtonEnabled = false;

        // Schritt 1: PaymentMethod erzeugen
        const { paymentMethod, error } = await this.stripe.createPaymentMethod({
            type: 'card',
            card: this.cardNumber,
            billing_details: {
                name: this.cardHolderName
            }
        });

        if (error) {
            console.error(error.message);
            alert('Fehler beim Erstellen der Payment Method: ' + error.message);
            return;
        }

        console.log('✅ PaymentMethod ID:', paymentMethod.id);

        //let payload : {} = {this.selectedPlan, paymentMethod};

        //let payload = { ...this.selectedPlan, ...paymentMethod };
        const payload = {
            "paymentMethodId": paymentMethod.id,
            "cardHolder": this.cardHolderName,
            "plan": this.selectedPlan.name,
            "yearly": this.selectedPlan.isYearly
        };



        //Schritt 2: ID ans Backend schicken
        this.paymentService.createSubscription(payload, paymentMethod.id).subscribe({
            next: (res) => {
                console.log('Subscription erstellt:', res);
                this.paymentState.emit(true);
            },
            error: (err) => {
                console.error(err);
            }
        });
    }



}
